import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    AirspaceAuthorisationDto,
    DisplayableMissionDto,
    LookupObject,
    OperationAuthorisation,
    PersonDto
} from '@flyfreely-portal-ui/flyfreely';
import { AirspaceAuthorisationState } from '../../interfaces';
import { AirspaceAuthorisationFormValue } from '../airspace-authorisation-preview.service';

/**
 * Displays the detail panel for one or more existing authorisations (right
 * panel of the preview dialogue).  Iterates `authorisations` and renders
 * the GCD table, status, conditions, cancel/close/delete/acknowledge buttons
 * and mission-details summary for each entry.
 *
 * Receives all data via `@Input()` and emits user-initiated actions via
 * `@Output()` so the parent container owns the side-effecting logic.
 *
 * `formValues` is nullable — if the preview form has not yet been populated
 * (e.g. no available authorisation types for this mission) the component
 * degrades gracefully and omits any sections that rely on form values.
 */
@Component({
    selector: 'authorisation-existing-detail',
    templateUrl: './authorisation-existing-detail.component.html'
})
export class AuthorisationExistingDetailComponent {
    @Input() authorisations: AirspaceAuthorisationDto[];
    @Input() formValues: AirspaceAuthorisationFormValue | null = null;
    @Input()
    airspaceAuthorisationType: AirspaceAuthorisationDto.AuthorisationType;
    @Input() airspaceAuthorisationStatus: AirspaceAuthorisationState;
    @Input() mission: DisplayableMissionDto;
    @Input() isInPast: boolean;
    @Input() visibleFormFields: string[] = [];
    @Input() collapseGcdTable: boolean;
    @Input() working: boolean;
    @Input() relatedMissionLookup: LookupObject<DisplayableMissionDto> = {};
    @Input() flightProfileName: string;

    @Output() cancel = new EventEmitter<OperationAuthorisation>();
    @Output() closeAuth = new EventEmitter<OperationAuthorisation>();
    @Output() delete = new EventEmitter<OperationAuthorisation>();
    @Output() acknowledgeRescinded = new EventEmitter<OperationAuthorisation>();
    @Output() collapseGcdTableChange = new EventEmitter<boolean>();
    @Output() showPilot = new EventEmitter<PersonDto>();
    @Output() goToRelatedMission = new EventEmitter<number>();

    toggleGcdTable() {
        this.collapseGcdTableChange.emit(!this.collapseGcdTable);
    }

    isFeet(unit: string): boolean {
        return unit?.toLowerCase() === 'feet';
    }

    getStatusCategory(
        status: AirspaceAuthorisationDto.Status
    ): 'PENDING' | 'TERMINATED' {
        switch (status) {
            case 'CANCELLED':
            case 'COMPLETED':
            case 'REJECTED':
                return 'TERMINATED';
            default:
                return 'PENDING';
        }
    }
}
